import type { ReportDefinitionChange } from './api';
import { ReportSchedule } from './report-schedule';
import type { ReportScheduleOptions } from './report-schedule-options';

/** Como uma linha do histórico aparece: a ação e o campo em português, e o valor legível. */
export class ReportDefinitionChangeLabel {
  private static readonly ACTIONS: Record<string, string> = {
    created: 'Criou',
    updated: 'Alterou',
    deleted: 'Excluiu',
  };
  private static readonly FIELDS: Record<string, string> = {
    definition: 'Relatório',
    status: 'Situação',
    schedule: 'Horário',
    recipients: 'Destinatários',
    templateName: 'Modelo',
  };
  private static readonly STATUSES: Record<string, string> = {
    active: 'Ativo',
    paused: 'Pausado',
  };
  /**
   * O valor de cada campo em frase. A criação e a exclusão gravam o relatório
   * inteiro em `definition`: a ação já diz o que houve, e o retrato não se mostra.
   */
  private static readonly VALUES: Record<string, (value: unknown) => string | null> = {
    definition: () => null,
    status: (value) => ReportDefinitionChangeLabel.STATUSES[String(value)] ?? String(value),
    schedule: (value) => new ReportSchedule(value as ReportScheduleOptions).describe(),
    recipients: (value) => ReportDefinitionChangeLabel.recipientCount(value as unknown[]),
  };

  static action(change: ReportDefinitionChange): string {
    return ReportDefinitionChangeLabel.ACTIONS[change.action] ?? change.action;
  }

  static field(change: ReportDefinitionChange): string {
    if (!change.field) return '';
    return ReportDefinitionChangeLabel.FIELDS[change.field] ?? change.field;
  }

  /** `null` quando a linha não mostra o valor. */
  static value(change: ReportDefinitionChange, value: unknown): string | null {
    if (value === null || value === undefined) return null;
    const describe = ReportDefinitionChangeLabel.VALUES[change.field ?? ''];
    if (describe) return describe(value);
    if (typeof value === 'string') return value;
    return JSON.stringify(value);
  }

  private static recipientCount(recipients: unknown[]): string {
    if (recipients.length === 1) return '1 destinatário';
    return `${recipients.length} destinatários`;
  }
}
